import React, { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import styled from "styled-components";
import { Loader } from "../../sharedUI/FullScreenLoader";
import IconButton from "../../sharedUI/IconButton";
import { fetcher } from "../../utils/fetcher";
import GuideCategoryForm from "../../components/forms/GuideCategoryForm";
import DeleteConfirmation from "../../components/forms/DeleteConfirmation";
import TagCard from "../../components/cards/TagCard";

const StyledGuideCategoriesPage = styled.div`
  .events-top {
    display: flex;
    justify-content: space-between;
    &-right {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      .search-input {
        margin: 0;
      }
    }
  }
  .entries {
    margin-top: 1rem;
  }
`;

const GuideCategoriesPage = () => {
  const outlet = useOutletContext();
  const { globalState, notify, modal, closeModal } = outlet;

  //State
  const [guideCategories, setGuideCategories] = useState([]);
  const [guideTags, setGuideTags] = useState([]);
  const [loading, setLoading] = useState(true);

  //Functions
  const getCategories = async () => {
    const { err, data } = await fetcher({
      path: "guideCategories/admin",
      auth: globalState.token,
    });
    if (err) {
      notify.error("Kunde inte hämta kategorier");
    } else {
      setGuideCategories(data);
    }
  };

  const getTags = async () => {
    const { err, data } = await fetcher({
      path: "guideTags",
      auth: globalState.token,
    });
    if (err) {
      notify.error("Kunde inte hämta taggar");
    } else {
      setGuideTags(data);
    }
  };

  const getData = async () => {
    setLoading(true);
    const promises = [getCategories(), getTags()];
    await Promise.all(promises);
    setLoading(false);
  };

  const createEntry = async (formData, cb, errCb) => {
    const { err } = await fetcher({
      path: "guideCategories",
      body: formData,
      method: "POST",
      auth: globalState.token,
    });
    if (err) {
      errCb();
      notify.error("Kunde inte skapa kategori");
      return;
    }
    cb();
    closeModal();
    getData();
  };

  const editEntry = async (formData, cb, errCb) => {
    const { err } = await fetcher({
      path: `guideCategories/${formData._id}`,
      body: formData,
      method: "POST",
      auth: globalState.token,
    });
    if (err) {
      errCb();
      notify.error("Kunde inte uppdatera kategori");
      return;
    }
    cb();
    closeModal();
    getData();
  };

  const deleteEntry = async (entry, cb, errCb) => {
    const { err } = await fetcher({
      path: `guideCategories/${entry._id}`,
      method: "DELETE",
      auth: globalState.token,
    });
    if (err) {
      errCb();
      notify.error("Kunde inte radera kategori");
      return;
    }
    cb();
    closeModal();
    getData();
  };

  const deleteEntryInit = (entry) => {
    modal(
      <DeleteConfirmation
        item={entry}
        deleteKey={"text"}
        handleInvalidKey={() => notify.error("Fyll i rätt värde i fältet")}
        action={deleteEntry}
        name="katagorin"
        instruction="kategorins namn"
      />,
      "Radera kategori"
    );
  };

  const editEntryInit = (entry) => {
    modal(
      <GuideCategoryForm
        data={entry}
        tags={guideTags}
        handleSubmit={editEntry}
        outlet={outlet}
      />,
      "Redigera kategori"
    );
  };

  const createEntryInit = () => {
    modal(
      <GuideCategoryForm
        tags={guideTags}
        handleSubmit={createEntry}
        outlet={outlet}
      />,
      "Skapa ny kategori"
    );
  };

  const sortedCategories = useMemo(() => {
    return [...guideCategories].sort((a, b) => {
      return a.sortIndex - b.sortIndex;
    });
  }, [guideCategories]);

  useEffect(() => {
    getData();
    // eslint-disable-next-line
  }, []);

  return (
    <StyledGuideCategoriesPage className="content ptop2">
      {loading ? (
        <Loader />
      ) : (
        <>
          <Link to="/guide">
            <IconButton trans iconLeft="chevron-left" className="mbottom1">
              Tillbaka till Övningsbank
            </IconButton>
          </Link>
          <div className="events-top">
            <h2 className="m0 h2">Övningsbanken kategorier</h2>
            <div className="events-top-right">
              <IconButton iconRight="plus" onClick={createEntryInit}>
                Skapa ny
              </IconButton>
            </div>
          </div>
          <div className="entries">
            {sortedCategories.length ? (
              sortedCategories.map((entry) => (
                <TagCard
                  key={entry._id}
                  entry={entry}
                  deleteInit={deleteEntryInit}
                  editInit={editEntryInit}
                />
              ))
            ) : (
              <p>Inga kategorier hittades</p>
            )}
          </div>
        </>
      )}
    </StyledGuideCategoriesPage>
  );
};

export default GuideCategoriesPage;
